import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  Avatar,
  CircularProgress,
  Autocomplete
} from '@mui/material';
import {
  VpnKey as VpnKeyIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { SwalConfig } from '../../../utils/swalConfig';

const AddUserRole = () => {
  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('user_id');
  
  const [formData, setFormData] = useState({
    id_user: userIdFromUrl || '',
    id_bu: '',
    id_client: '',
    role: ''
  });
  
  const [roleEntries, setRoleEntries] = useState([
    { id_bu: '', id_client: '', role: '' }
  ]);
  
  const roleOptions = [
    'admin',
    'superadmin', 
    'user',
    'manager',
    'supervisor',
    'operator',
    'viewer',
    'editor',
    'moderator',
    'guest'
  ];
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [buData, setBuData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const navigate = useNavigate();

  // Fetch Data
  const fetchData = async () => {
    try {
      setLoadingData(true);
      
      // Fetch Business Units
      const buResponse = await fetch('https://apidashboard.eurekagroup.id/bu', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (buResponse.ok) {
        const buResult = await buResponse.json();
        setBuData(buResult);
      }

      // Fetch Users
      const usersResponse = await fetch('https://api-sso-v2.eurekagroup.id/api/users', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (usersResponse.ok) {
        const usersResult = await usersResponse.json();
        setUsers(usersResult.data || usersResult);
      }

      // Fetch Clients
      const clientsResponse = await fetch('https://api-sso-v2.eurekagroup.id/api/clients', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (clientsResponse.ok) {
        const clientsResult = await clientsResponse.json();
        setClients(clientsResult.data || clientsResult);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Gagal memuat data'
      });
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleRoleEntryChange = (index, field, value) => {
    setRoleEntries(prev => 
      prev.map((entry, i) => 
        i === index ? { ...entry, [field]: value } : entry
      )
    );
  };

  const addRoleEntry = () => {
    setRoleEntries(prev => [...prev, { id_bu: '', id_client: '', role: '' }]);
  };

  const removeRoleEntry = (index) => {
    if (roleEntries.length > 1) {
      setRoleEntries(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validation
    if (!formData.id_user) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'User harus dipilih'
      });
      return;
    }

    // Validate role entries
    const validEntries = roleEntries.filter(entry => 
      entry.id_bu && entry.id_client && entry.role
    );

    if (validEntries.length === 0) {
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: 'Minimal satu role entry harus diisi dengan lengkap'
      });
      return;
    }

    try {
      setLoading(true);
      
      // Create multiple user roles
      const promises = validEntries.map(entry => 
        fetch('https://api-sso-v2.eurekagroup.id/api/user-roles', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({
            id_user: formData.id_user,
            id_bu: parseInt(entry.id_bu),
            id_client: entry.id_client,
            role: entry.role
          })
        })
      );

      const responses = await Promise.all(promises);
      
      // Check if all requests were successful
      const failedResponses = responses.filter(response => !response.ok);
      
      if (failedResponses.length > 0) {
        const errorData = await failedResponses[0].json();
        throw new Error(errorData.message || `HTTP error! status: ${failedResponses[0].status}`);
      }

      const results = await Promise.all(responses.map(response => response.json()));
      console.log('User roles created:', results);
      
      SwalConfig.success(
        'Berhasil!',
        `${validEntries.length} user role berhasil dibuat`,
        2000
      );
      
      navigate('/sso/user-role');
    } catch (error) {
      console.error('Error creating user roles:', error);
      Swal.fire({
        ...SwalConfig.error,
        title: 'Error!',
        text: `Gagal membuat user role: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };


  if (loadingData) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: 400,
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ color: 'text.secondary' }}>
          Memuat data...
        </Typography>
      </Box>
    );
  }

  return (
    <div className="animate-fade-in">
      <Card sx={{ boxShadow: '0 8px 32px rgba(0,0,0,0.1)', borderRadius: 3 }}>
        <CardHeader 
          title={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }} className="shadow-lg">
                <VpnKeyIcon />
              </Avatar>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }} className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  Tambah User Role
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} className="text-gray-600">
                  Buat role user untuk client SSO
                </Typography>
              </Box>
            </Box>
          }
          action={
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/sso/user-role')}
              sx={{
                borderRadius: 2,
                px: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 600
              }}
            >
              Kembali
            </Button>
          }
          sx={{ pb: 2 }}
        />
        <CardContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={users}
                  getOptionLabel={(option) => `${option.nama_lengkap} (${option.email})`}
                  value={users.find(user => user._id === formData.id_user) || null}
                  onChange={(event, newValue) => {
                    setFormData(prev => ({
                      ...prev,
                      id_user: newValue ? newValue._id : ''
                    }));
                  }}
                  disabled={!!userIdFromUrl}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="User"
                      variant="outlined"
                      required
                      placeholder={userIdFromUrl ? "User sudah dipilih" : "Cari user..."}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2
                        }
                      }}
                    />
                  )}
                  sx={{
                    '& .MuiAutocomplete-root': {
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                  Role Assignments
                </Typography>
                {roleEntries.map((entry, index) => (
                  <Box key={index} sx={{ 
                    p: 3, 
                    mb: 2, 
                    border: '1px solid', 
                    borderColor: 'grey.300', 
                    borderRadius: 2,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'action.hover'
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Role #{index + 1}
                      </Typography>
                      {roleEntries.length > 1 && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => removeRoleEntry(index)}
                          sx={{ minWidth: 'auto', px: 1 }}
                        >
                          Hapus
                        </Button>
                      )}
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          options={clients}
                          getOptionLabel={(option) => option.client}
                          value={clients.find(client => client._id === entry.id_client) || null}
                          onChange={(event, newValue) => {
                            handleRoleEntryChange(index, 'id_client', newValue ? newValue._id : '');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Client"
                              variant="outlined"
                              required
                              placeholder="Cari client..."
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          )}
                          sx={{
                            '& .MuiAutocomplete-root': {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }
                          }}
                        />
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <FormControl fullWidth variant="outlined" required size="small">
                          <InputLabel>Business Unit</InputLabel>
                          <Select
                            value={entry.id_bu}
                            onChange={(event) => handleRoleEntryChange(index, 'id_bu', event.target.value)}
                            label="Business Unit"
                            sx={{ borderRadius: 2 }}
                          >
                            {buData.map((bu) => (
                              <MenuItem key={bu._id} value={bu.bu_code}>
                                {bu.bu_name} ({bu.bu_code})
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      
                      <Grid item xs={12} md={4}>
                        <Autocomplete
                          freeSolo
                          options={roleOptions}
                          value={entry.role}
                          onChange={(event, newValue) => {
                            handleRoleEntryChange(index, 'role', newValue || '');
                          }}
                          onInputChange={(event, newInputValue) => {
                            handleRoleEntryChange(index, 'role', newInputValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Role"
                              variant="outlined"
                              required
                              placeholder="Cari atau ketik role..."
                              size="small"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          )}
                          sx={{
                            '& .MuiAutocomplete-root': {
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2
                              }
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                ))}
                
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addRoleEntry}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    textTransform: 'none',
                    fontWeight: 600,
                    borderStyle: 'dashed',
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    '&:hover': {
                      borderColor: 'primary.dark',
                      bgcolor: 'primary.light',
                      color: 'primary.dark'
                    }
                  }}
                >
                  Tambah Role Lain
                </Button>
              </Grid>
              
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/sso/user-role')}
                    disabled={loading}
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600
                    }}
                  >
                    Batal
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transform hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-xl"
                    sx={{
                      borderRadius: 2,
                      px: 3,
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        transform: 'translateY(-2px)'
                      }
                    }}
                  >
                    {loading ? 'Menyimpan...' : `Simpan ${roleEntries.length} User Role${roleEntries.length > 1 ? 's' : ''}`}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </div>
  );
};

export default AddUserRole;
